import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { CATEGORY_IDS, CATEGORY_ICONS, BADGE_IDS, BADGE_EMOJIS } from "../lib/constants";
import { ArrowRight, Heart, ShieldCheck, Users, HandHeart, Sparkles } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useT } from "../i18n/I18nContext";

const GOFUNDME_URL = "https://gofund.me/e11966896";

const PRINCIPLE_KEYS = [
  { key: "free", icon: Heart },
  { key: "voluntary", icon: HandHeart },
  { key: "respect", icon: Users },
  { key: "trust", icon: ShieldCheck },
  { key: "community", icon: Sparkles },
];

export default function Landing() {
  const { user } = useAuth();
  const t = useT();
  const [stats, setStats] = useState({ members: 0, completed_helps: 0, open_requests: 0 });

  useEffect(() => {
    let cancelled = false;
    api.get("/stats")
      .then(({ data }) => { if (!cancelled) setStats(data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  return (
    <div data-testid="landing-page">
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 pt-16 md:pt-24 pb-20 md:pb-28 grid lg:grid-cols-2 gap-12 items-center">
          <div className="anim-fade-up">
            <div className="flex items-center gap-3 mb-5">
              <img
                src="https://customer-assets.emergentagent.com/job_solidarity-hub-15/artifacts/133hdsq6_425270B1-F179-4AB5-AD31-7D104533D284.jpeg"
                alt="MABW logo"
                className="w-14 h-14 rounded-2xl shadow-mabw-soft"
              />
              <div className="text-xs uppercase tracking-[0.22em] text-mabw-secondary font-semibold">
                {t("landing.overline")}
              </div>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight font-semibold" style={{ fontFamily: "Outfit" }}>
              {t("landing.hero_title_1")} <span className="text-mabw-primary">{t("landing.hero_title_2")}</span>{t("landing.hero_title_3")}
            </h1>
            <p className="mt-6 text-lg text-mabw-soft max-w-xl leading-relaxed">
              {t("landing.hero_subtitle")}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              {user ? (
                <Link to="/app" data-testid="landing-go-app" className="btn-mabw">
                  {t("landing.cta_enter_app")} <ArrowRight className="w-4 h-4" />
                </Link>
              ) : (
                <>
                  <Link to="/registrati" data-testid="landing-cta-register" className="btn-mabw">
                    {t("landing.cta_join")} <ArrowRight className="w-4 h-4" />
                  </Link>
                  <Link to="/login" data-testid="landing-cta-login" className="btn-mabw-secondary">
                    {t("landing.cta_have_account")}
                  </Link>
                </>
              )}
            </div>

            <div className="mt-12 grid grid-cols-3 gap-6 max-w-md">
              <Stat label={t("landing.members")} value={stats.members} testid="stat-members" />
              <Stat label={t("landing.completed_helps")} value={stats.completed_helps} testid="stat-completed" />
              <Stat label={t("landing.open_requests")} value={stats.open_requests} testid="stat-open" />
            </div>
          </div>

          <div className="relative anim-fade-up">
            <div className="absolute -inset-6 rounded-[2.5rem] bg-mabw-accent/20 -rotate-2" aria-hidden />
            <img
              src="https://images.unsplash.com/photo-1721059050924-eaf014837bd1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHwzfHxjb21tdW5pdHklMjBoZWxwaW5nJTIwaGFuZHN8ZW58MHx8fHwxNzgxMjg4MTk0fDA&ixlib=rb-4.1.0&q=85"
              alt="Community helping hands"
              className="relative w-full h-[520px] object-cover rounded-[2rem] shadow-mabw-hover"
              data-testid="landing-hero-image"
            />
            <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-mabw-soft p-4 border border-mabw flex items-center gap-3 max-w-[260px]">
              <span className="w-10 h-10 rounded-full bg-mabw-secondary/15 flex items-center justify-center text-mabw-secondary">
                <HandHeart className="w-5 h-5" />
              </span>
              <div>
                <div className="text-sm font-semibold">{t("landing.in_person_title")}</div>
                <div className="text-xs text-mabw-soft">{t("landing.in_person_desc")}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28 bg-white border-y border-mabw">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="max-w-2xl">
            <div className="text-xs uppercase tracking-[0.22em] text-mabw-secondary font-semibold mb-3">
              {t("landing.principles_overline")}
            </div>
            <h2 className="text-3xl md:text-4xl font-semibold leading-tight" style={{ fontFamily: "Outfit" }}>
              {t("landing.principles_title")}
            </h2>
          </div>
          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-5 gap-4">
            {PRINCIPLE_KEYS.map((p) => (
              <div key={p.key} className="card-mabw p-6">
                <div className="w-10 h-10 rounded-full bg-mabw-primary/10 text-mabw-primary flex items-center justify-center mb-4">
                  <p.icon className="w-5 h-5" />
                </div>
                <div className="font-semibold text-lg" style={{ fontFamily: "Outfit" }}>
                  {t(`landing.principles.${p.key}.t`)}
                </div>
                <p className="text-sm text-mabw-soft mt-2 leading-relaxed">
                  {t(`landing.principles.${p.key}.d`)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
            <div className="max-w-2xl">
              <div className="text-xs uppercase tracking-[0.22em] text-mabw-secondary font-semibold mb-3">
                {t("landing.cat_overline")}
              </div>
              <h2 className="text-3xl md:text-4xl font-semibold leading-tight" style={{ fontFamily: "Outfit" }}>
                {t("landing.cat_title")}
              </h2>
            </div>
            <p className="text-sm text-mabw-soft max-w-md">{t("landing.cat_sub")}</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {CATEGORY_IDS.map((id) => {
              const Icon = CATEGORY_ICONS[id];
              return (
                <div key={id} data-testid={`cat-${id}`} className="card-mabw p-6 hover:-translate-y-0.5 transition-transform">
                  <Icon className="w-6 h-6 text-mabw-primary" />
                  <div className="mt-4 font-semibold" style={{ fontFamily: "Outfit" }}>
                    {t(`categories.${id}.label`)}
                  </div>
                  <div className="text-sm text-mabw-soft mt-1">{t(`categories.${id}.hint`)}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28 bg-stone-50 border-y border-mabw">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="max-w-2xl">
            <div className="text-xs uppercase tracking-[0.22em] text-mabw-secondary font-semibold mb-3">
              {t("landing.badges_overline")}
            </div>
            <h2 className="text-3xl md:text-4xl font-semibold leading-tight" style={{ fontFamily: "Outfit" }}>
              {t("landing.badges_title")}
            </h2>
          </div>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {BADGE_IDS.map((id) => (
              <div key={id} className="card-mabw p-6">
                <div className="text-3xl mb-3">{BADGE_EMOJIS[id]}</div>
                <div className="font-semibold" style={{ fontFamily: "Outfit" }}>
                  {t(`badges.${id}.name`)}
                </div>
                <div className="text-sm text-mabw-soft mt-1">{t(`badges.${id}.desc`)}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 md:py-20">
        <div className="max-w-5xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="card-mabw p-8 md:p-12 grid md:grid-cols-[auto_1fr_auto] items-center gap-8" data-testid="support-section">
            <div className="w-16 h-16 rounded-2xl bg-mabw-secondary/15 text-mabw-secondary flex items-center justify-center mx-auto md:mx-0">
              <Heart className="w-8 h-8" />
            </div>
            <div className="text-center md:text-left">
              <div className="text-xs uppercase tracking-[0.22em] text-mabw-secondary font-semibold mb-2">
                {t("landing.support_overline")}
              </div>
              <h3 className="text-2xl md:text-3xl font-semibold leading-tight" style={{ fontFamily: "Outfit" }}>
                {t("landing.support_title")}
              </h3>
              <p className="text-sm md:text-base text-mabw-soft mt-3 leading-relaxed">
                {t("landing.support_desc")}
              </p>
            </div>
            <a
              href={GOFUNDME_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="support-cta"
              className="btn-mabw whitespace-nowrap mx-auto md:mx-0"
            >
              <Heart className="w-4 h-4" /> {t("landing.support_cta")}
            </a>
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28">
        <div className="max-w-5xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="card-mabw p-10 md:p-14 text-center bg-gradient-to-br from-white to-stone-50">
            <h2 className="text-3xl md:text-4xl font-semibold leading-tight" style={{ fontFamily: "Outfit" }}>
              {t("landing.cta_box_title")}
            </h2>
            <p className="mt-4 text-mabw-soft max-w-2xl mx-auto">{t("landing.cta_box_desc")}</p>
            <div className="mt-8 flex flex-wrap gap-3 justify-center">
              {!user && (
                <>
                  <Link to="/registrati" data-testid="cta-register-bottom" className="btn-mabw">
                    {t("landing.cta_create")} <ArrowRight className="w-4 h-4" />
                  </Link>
                  <Link to="/login" data-testid="cta-login-bottom" className="btn-mabw-secondary">
                    {t("landing.cta_login")}
                  </Link>
                </>
              )}
              {user && (
                <Link to="/app/cerca" data-testid="cta-browse" className="btn-mabw">
                  {t("landing.cta_browse")} <ArrowRight className="w-4 h-4" />
                </Link>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function Stat({ label, value, testid }) {
  return (
    <div data-testid={testid}>
      <div className="text-3xl font-semibold text-mabw-primary" style={{ fontFamily: "Outfit" }}>{value}</div>
      <div className="text-xs uppercase tracking-[0.18em] text-mabw-soft mt-1">{label}</div>
    </div>
  );
}
